'use client';

import { useAuiState } from '@assistant-ui/react';
import { useSyncExternalStore, type FC } from 'react';

import { cn } from '@/lib/utils';

/** One shared ticker for every visible timestamp (re-renders once a minute). */
const listeners = new Set<() => void>();
let now = Date.now();
let timer: ReturnType<typeof setInterval> | null = null;

function subscribe(listener: () => void) {
  listeners.add(listener);
  if (!timer) {
    now = Date.now();
    timer = setInterval(() => {
      now = Date.now();
      listeners.forEach(l => l());
    }, 30_000);
  }
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0 && timer) {
      clearInterval(timer);
      timer = null;
    }
  };
}

const getNow = () => now;

const relativeFormat = new Intl.RelativeTimeFormat('fa', { numeric: 'auto' });
const dayMonthFormat = new Intl.DateTimeFormat('fa-IR', {
  day: 'numeric',
  month: 'long'
});
const dayMonthYearFormat = new Intl.DateTimeFormat('fa-IR', {
  day: 'numeric',
  month: 'long',
  year: 'numeric'
});
const yearFormat = new Intl.DateTimeFormat('fa-IR', { year: 'numeric' });
const fullFormat = new Intl.DateTimeFormat('fa-IR', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit'
});

function startOfDay(ms: number): number {
  const d = new Date(ms);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

export function formatMessageTime(date: Date, nowMs: number): string {
  const diffMs = Math.max(0, nowMs - date.getTime());
  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 1) return 'همین الان';
  if (minutes < 60) return relativeFormat.format(-minutes, 'minute');

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return relativeFormat.format(-hours, 'hour');

  const days = Math.round((startOfDay(nowMs) - startOfDay(date.getTime())) / 86_400_000);
  if (days === 1) return 'دیروز';

  const sameYear =
    yearFormat.format(date) === yearFormat.format(new Date(nowMs));
  return (sameYear ? dayMonthFormat : dayMonthYearFormat).format(date);
}

export const MessageTimestamp: FC<{ className?: string }> = ({ className }) => {
  const createdAt = useAuiState(s => s.message.createdAt);
  const nowMs = useSyncExternalStore(subscribe, getNow, getNow);

  if (!createdAt || Number.isNaN(createdAt.getTime())) return null;

  return (
    <time
      dir="rtl"
      dateTime={createdAt.toISOString()}
      title={fullFormat.format(createdAt)}
      className={cn(
        'aui-message-timestamp text-muted-foreground select-none whitespace-nowrap px-1 text-[0.7rem]',
        className
      )}
    >
      {formatMessageTime(createdAt, nowMs)}
    </time>
  );
};
